import { AccountForm, type AccountFormValues } from '@/components/accounts/AccountForm';
import {
  AddAccountPalette,
  type AddAccountSelection,
} from '@/components/accounts/AddAccountPalette';
import { Button } from '@/components/ui/button';
import type {
  AddAccountCategory,
  AddAccountCategoryId,
} from '@/features/accounts/addAccountCategories';
import { useConnectPlaid } from '@/features/plaid/useConnectPlaid';
import { useLanguage } from '@/i18n/language-hooks';
import { ArrowLeft } from 'lucide-react';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

type AddAccountFlowProps = {
  onSubmit: (data: AccountFormValues) => Promise<void>;
  onCancel: () => void;
  /**
   * `aside` trims the palette for the sliding detail panel; `page` keeps the
   * full-size command surface used on `/banking/accounts/new`.
   */
  variant?: 'page' | 'aside';
  /**
   * Controlled manual-category step. When provided with
   * {@link onManualCategoryChange}, the host (e.g. page hero Back) owns the step.
   */
  manualCategory?: AddAccountCategory | null;
  onManualCategoryChange?: (category: AddAccountCategory | null) => void;
  /** Hide the in-flow back control when the host already renders one (page hero). */
  hideBackButton?: boolean;
  /** Called before navigating to the Plaid review route (e.g. close the aside). */
  onBeforeAutomatedNavigate?: () => void;
  /** Open the palette already drilled into this category (e.g. investment on trade). */
  initialCategoryId?: AddAccountCategoryId;
  /** Category catalog for the palette home. Defaults to the full banking set. */
  categories?: AddAccountCategory[];
  /** Require UK tax wrapper on the manual form (trade add-account). */
  enableProductWrapper?: boolean;
};

/**
 * Shared add-account wizard: pick a category (and optionally connect via Plaid),
 * then fall through to the manual form. Used by the full-page route and the
 * desktop sliding aside so both surfaces offer the same manual/automatic choice.
 */
export function AddAccountFlow({
  onSubmit,
  onCancel,
  variant = 'page',
  manualCategory: controlledCategory,
  onManualCategoryChange,
  hideBackButton = false,
  onBeforeAutomatedNavigate,
  initialCategoryId,
  categories,
  enableProductWrapper = false,
}: AddAccountFlowProps) {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const { connect, isConnecting } = useConnectPlaid();
  const [uncontrolledCategory, setUncontrolledCategory] = useState<AddAccountCategory | null>(null);

  const isControlled = controlledCategory !== undefined;
  const manualCategory = isControlled ? controlledCategory : uncontrolledCategory;

  const selectManualCategory = (category: AddAccountCategory | null) => {
    if (!isControlled) setUncontrolledCategory(category);
    onManualCategoryChange?.(category);
  };

  const handleSelect = async (selection: AddAccountSelection) => {
    if (selection.kind === 'manual') {
      selectManualCategory(selection.category);
      return;
    }
    const item = await connect();
    if (item) {
      onBeforeAutomatedNavigate?.();
      void navigate(`/banking/accounts/plaid?itemId=${encodeURIComponent(item.itemId)}`);
    }
  };

  if (manualCategory) {
    return (
      <div className='flex flex-col gap-4'>
        {!hideBackButton ? (
          <Button
            type='button'
            variant='outline'
            size='sm'
            className='self-start'
            onClick={() => selectManualCategory(null)}
          >
            <ArrowLeft className='h-4 w-4' aria-hidden />
            {t('accounts.add.palette.back')}
          </Button>
        ) : null}
        <AccountForm
          allowedTypes={manualCategory.types}
          enableProductWrapper={enableProductWrapper}
          onSubmit={onSubmit}
          onCancel={onCancel}
        />
      </div>
    );
  }

  return (
    <AddAccountPalette
      variant={variant}
      isConnecting={isConnecting}
      initialCategoryId={initialCategoryId}
      categories={categories}
      onSelect={(selection) => void handleSelect(selection)}
    />
  );
}
